import { captureException } from '@sentry/react'
import classNames from 'classnames'
import { FieldArray, Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { MdAdd } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { FileWithPath } from 'react-dropzone'
import type ReactPlayer from 'react-player'
import type * as Yup from 'yup'
import type { QUALITY_IDENTIFIER } from '~/const/asset'
import type { IAssetFileQuality, IAssetFileWithQualifier, IAssetProperty } from '~/types/assets-management'

import PropertiesField from '../AssetsListing/shared/PropertiesField'

import Button from '~/components/Buttons/Button'
import LinkButton from '~/components/Buttons/LinkButton'
import Dropzone from '~/components/Dropzone'
import FilePreview from '~/components/FilePreview'
import DateInput from '~/components/Inputs/Formik/DateInput'
import SelectInput from '~/components/Inputs/Formik/SelectInput'
import TextInput from '~/components/Inputs/Formik/TextInput'
import Label from '~/components/Label'
import { ModalBody, ModalFooter } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import RequiredInformation from '~/components/RequiredInformation'
import { fileTypeOptions, statusOptions as defaultStatusOptions } from '~/const/asset'
import { useAssetsTagsKeysQuery } from '~/queries/assets'
import { useCreateAssetFileMutation, useUploadFileMutation } from '~/queries/files'
import { getQualityIdentifierFromFilename, getKeysOptions } from '~/utils/asset'
import { reactSelectMenuPositionDef } from '~/utils/options'

import './styles.scss'

interface Props {
  assetFiles?: IAssetFileWithQualifier[]
  disabledFields?: (keyof Values)[]
  initialValues: Values
  modalId: string
  onCancel: VoidFunction
  onManageAssetClick?: VoidFunction
  onSubmit: (data: Values, files: IAssetFileQuality[]) => void
  onVideoPreviewReady?: (player: ReactPlayer) => void
  statusOptions?: IOption[]
  validationSchema: Yup.ObjectSchema<Yup.AnyObject>
  withAssetUploader?: boolean
  withDropzone?: boolean
}

export interface Values {
  expirationDate: string
  fileType: IOption | null
  name: string
  properties: IAssetProperty[]
  status: IOption
}

const AssetForm = ({
  assetFiles,
  disabledFields,
  initialValues,
  modalId,
  onCancel,
  onManageAssetClick,
  onSubmit,
  statusOptions = defaultStatusOptions,
  validationSchema,
  withAssetUploader = true,
  withDropzone = true,
}: Props): ReactElement => {
  const { mutateAsync: createAssetFileMutation } = useCreateAssetFileMutation()

  const { mutateAsync: uploadFileMutation } = useUploadFileMutation()

  const { data: tagsKeys, isLoading: isAssetTagsKaysLoading } = useAssetsTagsKeysQuery()

  const [files, setFiles] = useState<IAssetFileWithQualifier[]>(assetFiles || [])

  const [isLoading, setIsLoading] = useState(false)

  const [keysOptions, setKeysOptions] = useState(getKeysOptions(tagsKeys))

  const formContainerClassnames = classNames('asset-form-container', { 'with-asset-uploader': withAssetUploader })

  const handleSubmit = (data: Values) => {
    onSubmit(
      data,
      files.map(({ file_id, qualifier }) => {
        return { file_id: file_id, qualifier: qualifier }
      })
    )
  }

  const handleMedia = async (newFiles: FileWithPath[]) => {
    const uploadFile = async (file: FileWithPath) => {
      const { file_id, upload_url } = await createAssetFileMutation({
        mimeType: file.type,
        name: file.name,
        size: file.size,
      })

      await uploadFileMutation({
        file,
        mimeType: file.type,
        uploadUrl: upload_url,
      })
      return {
        ...file,
        file_id,
        objectURL: URL.createObjectURL(file),
        qualifier: getQualityIdentifierFromFilename(file.name),
        type: file.type,
      }
    }

    try {
      setIsLoading(true)
      const assetFileQuality = await Promise.all(
        newFiles.map(async (file) => {
          return uploadFile(file)
        })
      )
      setIsLoading(false)
      setFiles([...files, ...assetFileQuality])
    } catch (e) {
      captureException(e)
    }
  }

  const handleFilePreviewRemove = (index: number) => {
    files.splice(index, 1)
    setFiles([...files])
  }

  const handleQualityIdentifierChange = (qualityIdentifier: QUALITY_IDENTIFIER, fileId: string) => {
    const selectedFileIndex = files.findIndex((file) => file.file_id === fileId)

    if (selectedFileIndex != -1) {
      files[selectedFileIndex].qualifier = qualityIdentifier
    }
    setFiles(files)
  }

  useEffect(() => {
    if (tagsKeys) {
      setKeysOptions(getKeysOptions(tagsKeys))
    }
  }, [tagsKeys])

  if (isAssetTagsKaysLoading) {
    return <MwMLoader category="item" />
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ isSubmitting, values }) => (
        <Form className={formContainerClassnames}>
          <ModalBody className="asset-form-container__body">
            {withAssetUploader ? (
              <div className="asset-uploader">
                <div className="title-container">
                  <Label className="fieldset-title" label="Upload files" />
                  {onManageAssetClick ? (
                    <LinkButton backgroundColor="light" label="Manage" onClick={onManageAssetClick} type="internal" />
                  ) : null}
                </div>
                {withDropzone ? <Dropzone isLoading={isLoading} multiple onDrop={handleMedia} /> : null}
                <div className="uploaded-media">
                  {files.map((file, index) => {
                    return (
                      <FilePreview
                        className="small"
                        isEditable={withDropzone}
                        key={index}
                        mimeType={file.type}
                        name={file.path || ''}
                        onQualityIdentifierChange={(qualityIdentifier) =>
                          handleQualityIdentifierChange(qualityIdentifier, file.file_id)
                        }
                        onRemove={withDropzone ? () => handleFilePreviewRemove(index) : undefined}
                        qualityIdentifier={file.qualifier}
                        src={file.objectURL}
                      />
                    )
                  })}
                </div>
              </div>
            ) : null}
            <div className="asset-details">
              <Label className="fieldset-title" label="Details" />
              <RequiredInformation />
              <TextInput label="Name" name="name" placeholder="Type a name" required />
              <SelectInput
                {...reactSelectMenuPositionDef(modalId)}
                disabled={disabledFields?.includes('fileType')}
                label="Type"
                name="fileType"
                options={fileTypeOptions}
                placeholder="Select the asset type"
                required
              />
              <SelectInput
                {...reactSelectMenuPositionDef(modalId)}
                disabled={disabledFields?.includes('status')}
                label="Status"
                name="status"
                options={statusOptions}
                placeholder="Select the asset status"
                required
              />
              <DateInput label="Expiration date" name="expirationDate" placeholder="Expiration date" />
              <FieldArray
                name="properties"
                render={(arrayHelpers) => (
                  <div className="asset-properties">
                    <div className="asset-properties__title-container">
                      <Label className="fieldset-title" label="Properties" required />
                      <Button color="primary" onClick={() => arrayHelpers.push({ key: null, value: '' })} size="small">
                        <MdAdd color="#fff" size={14} />
                      </Button>
                    </div>
                    {values.properties.map(({ key }, index) => (
                      <PropertiesField
                        index={index}
                        key={`${key}-${index}`}
                        modalId={modalId}
                        onDelete={() => {
                          if (values.properties.length > 1) {
                            arrayHelpers.remove(index)
                          }
                        }}
                        selectOptions={keysOptions}
                      />
                    ))}
                  </div>
                )}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button ghost onClick={onCancel}>
              Cancel
            </Button>
            <Button disabled={isSubmitting || isLoading} type="submit">
              {isLoading && <div className="lds-dual-ring" />}
              Save
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  )
}

export default AssetForm
